import { useEffect, useMemo, useState } from "react"
import { showDialogInfo } from "@/hooks/use-dialog-info"
import { Inspiration } from "@/types/inspiration"
import { NotificationEventData } from "@/types/socket"
import { MapViewport } from "@/types/util"
import { useMap } from "@vis.gl/react-google-maps"

import { buildClientAxios } from "@/lib/client-axios"
import { readMapViewport } from "@/lib/gmap-utils"
import { InspirationApi } from "@/lib/inspiration-api"
import { NotificationManager } from "@/lib/notification-manager"

/**
 * Registry id for an inspiration's maps. All map surfaces for a guide
 * (inline section, expanded fullscreen, desktop side map) share this id, so
 * `useMap(inspirationMapId(ref))` returns whichever is currently mounted.
 */
export function inspirationMapId(entityRef: string) {
  return `inspiration-map-${entityRef}`
}

/**
 * Returns a getter that reads the live viewport of an inspiration's map at
 * call time (e.g. when the user clicks "Download PDF"). Returns null when no
 * map is mounted, letting PDF generation fall back to marker auto-fit.
 */
export function useInspirationMapViewport(entityRef?: string) {
  const map = useMap(entityRef ? inspirationMapId(entityRef) : null)

  return (): MapViewport | null => {
    if (!entityRef) return null

    return readMapViewport(map)
  }
}

export function useInspirationPdf() {
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const inspirationApi = useMemo(
    () => new InspirationApi(buildClientAxios()),
    []
  )

  const downloadPdf = async (
    inspiration: Inspiration,
    mapViewport?: MapViewport | null
  ) => {
    if (isGenerating) {
      console.warn("PDF generation already in progress")
      return false
    }

    setIsGenerating(true)
    setError(null)

    try {
      const jobResponse = await inspirationApi.downloadInspirationPdfAsync(
        inspiration.entity_ref,
        mapViewport
      )
      if (jobResponse) {
        if (jobResponse.existing_job) {
          showDialogInfo({
            title: "PDF Generation In Progress",
            message:
              "A PDF for this inspiration is already being generated. You will receive a notification when it's ready.",
            okLabel: "OK",
            okVariant: "primary",
          })
        } else {
          showDialogInfo({
            title: "Generating PDF",
            message:
              "Your PDF is being generated. This may take a minute. You will receive a notification when it's ready to download.",
            okLabel: "OK",
            okVariant: "primary",
          })
        }
      }

      return true
    } catch (err) {
      console.error("PDF generation failed:", err)
      const errorMessage =
        err instanceof Error ? err.message : "Failed to start PDF generation"
      setError(errorMessage)
      setIsGenerating(false) // Reset state on error
      return false
    }
  }

  useEffect(() => {
    const handleNotification = (data: NotificationEventData) => {
      if (data.notification.type === "inspiration_pdf_ready") {
        setIsGenerating(false)
      }
    }

    NotificationManager.instance.addSubscriber(handleNotification)

    return () => {
      NotificationManager.instance.removeSubscriber(handleNotification)
      setIsGenerating(false)
    }
  }, [])

  return {
    downloadPdf,
    isGenerating,
    error,
  }
}
